import React, { useState, useEffect } from 'react';
import NavigationBar from '../../components/NavigationBar';
import CalendarGrid from './components/CalendarGrid';
import CalendarHeader from './components/CalendarHeader';
import EventLegend from './components/EventLegend';
import EventEditPopover from './components/EventEditPopover';
import { appointmentsService } from '../../services/crmService';
import { getCurrentDate, toIndiaTime } from '../../utils/dateUtils';


const CalendarView = () => {
  const [currentDate, setCurrentDate] = useState(getCurrentDate());
  const [selectedFilters, setSelectedFilters] = useState({
    appointments: true,
    deliveries: true
  });
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [editPopoverPosition, setEditPopoverPosition] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const handleRealtimeUpdate = ({ eventType, new: newData, old: oldData }) => {
    if (eventType === 'INSERT') {
      setEvents(prev => [...prev, { ...newData, date: toIndiaTime(newData?.date) }]);
    } else if (eventType === 'UPDATE') {
      setEvents(prev => prev?.map(evt => 
        evt?.id === newData?.id ? { ...newData, date: toIndiaTime(newData?.date) } : evt
      ));
    } else if (eventType === 'DELETE') {
      setEvents(prev => prev?.filter(evt => evt?.id !== oldData?.id));
    }
  };

  useEffect(() => {
    loadAppointments();
    const unsubscribe = appointmentsService?.subscribeToChanges(handleRealtimeUpdate);
    return () => unsubscribe();
  }, []);

  const loadAppointments = async () => {
    try {
      setLoading(true);
      const data = await appointmentsService?.getAll();
      const formattedEvents = data?.map(apt => ({
        ...apt,
        date: toIndiaTime(apt?.date)
      }));
      setEvents(formattedEvents);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load appointments');
      console.error('Error loading appointments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviousMonth = () => {
    const indiaDate = getCurrentDate();
    setCurrentDate(new Date(currentDate?.getFullYear(), currentDate?.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    const indiaDate = getCurrentDate();
    setCurrentDate(new Date(currentDate?.getFullYear(), currentDate?.getMonth() + 1, 1));
  };

  const handleToday = () => {
    setCurrentDate(getCurrentDate());
  };

  const handleFilterToggle = (filterType) => {
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]: !prev?.[filterType]
    }));
  };

  const handleEventClick = (event, position) => {
    setSelectedEvent(event);
    setEditPopoverPosition(position);
  };

  const handleEventUpdate = async (updatedEvent) => {
    try {
      const eventData = {
        ...updatedEvent,
        date: updatedEvent?.date instanceof Date 
          ? updatedEvent?.date?.toISOString()?.split('T')?.[0] 
          : updatedEvent?.date
      };
      await appointmentsService?.update(updatedEvent?.id, eventData);
      setSelectedEvent(null);
      setEditPopoverPosition(null);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to update appointment');
      console.error('Error updating appointment:', err);
    }
  };

  const handleEventDelete = async (eventId) => {
    try {
      await appointmentsService?.delete(eventId);
      setSelectedEvent(null);
      setEditPopoverPosition(null);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to delete appointment');
      console.error('Error deleting appointment:', err);
    }
  };

  const handleDateClick = (date) => {
    // Quick add functionality - could open a modal for new event creation
    console.log('Date clicked for quick add:', date);
  };

  const filteredEvents = events?.filter(event => {
    if (event?.type === 'appointment' && !selectedFilters?.appointments) return false;
    if (event?.type === 'delivery' && !selectedFilters?.deliveries) return false;
    return true;
  });

  return (
    <div className="min-h-screen bg-background pt-20">
      <NavigationBar />
      
      {error && (
        <div className="bg-error/10 border border-error/30 text-error px-4 py-3 rounded-lg mx-4 mt-4">
          <p>{error}</p>
        </div>
      )}
      
      <div className="container mx-auto px-4 py-6 md:py-8 lg:py-12">
        <div className="mb-6 md:mb-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">Calendar View</h1>
          <p className="text-base md:text-lg text-muted-foreground">Manage appointments and delivery dates</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading appointments...</p>
            </div>
          </div>
        ) : (
          <div className="bg-card rounded-2xl shadow-elevation-2 p-4 md:p-6 lg:p-8">
            <CalendarHeader
              currentDate={currentDate}
              onPreviousMonth={handlePreviousMonth}
              onNextMonth={handleNextMonth}
              onToday={handleToday}
              selectedFilters={selectedFilters}
              onFilterToggle={handleFilterToggle}
            />

            <EventLegend />

            <CalendarGrid
              currentDate={currentDate}
              events={filteredEvents}
              onEventClick={handleEventClick}
              onDateClick={handleDateClick}
            />
          </div>
        )}
      </div>

      {selectedEvent && editPopoverPosition && (
        <EventEditPopover
          event={selectedEvent}
          position={editPopoverPosition}
          onClose={() => {
            setSelectedEvent(null);
            setEditPopoverPosition(null);
          }}
          onUpdate={handleEventUpdate}
          onDelete={handleEventDelete}
        />
      )}
    </div>
  );
};

export default CalendarView;